import { useMutation } from "react-query";
import { verifyClientDocument } from "api/customers";
import { useGlobalToast } from "components/GlobalToastProvider";

interface UseVerifyClientDocumentOptions {
  onSuccess?: () => void;
}

interface UseVerifyClientDocumentReturn {
  verifyDocument: (documentId: string) => void;
  verifyIsLoading: boolean;
}

/**
 * Shared hook for verifying a client document via PUT v3/documents/:documentId/verify.
 * The backend resolves the client ID from the document ID.
 * @param options - Optional callbacks: onSuccess.
 */
const useVerifyClientDocument = (
  options: UseVerifyClientDocumentOptions = {}
): UseVerifyClientDocumentReturn => {
  const { showToast } = useGlobalToast();

  const { mutate: verifyDocument, isLoading: verifyIsLoading } = useMutation(
    (documentId: string) => verifyClientDocument(documentId),
    {
      onSuccess: () => {
        if (options.onSuccess) options.onSuccess();
      },
      onError: (error: unknown, documentId: string) => {
        showToast({
          message: "Whoops! Something went wrong",
          errorState: true,
          retryHandler: () => verifyDocument(documentId),
        });
        console.error("Error verifying document:", error);
      },
    }
  );

  return { verifyDocument, verifyIsLoading };
};

export default useVerifyClientDocument;
